// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Error from "../layout/pages/404";
import { getActiveRoutesV2, routes } from "../route";
import Layout from "../layout/Layout";
import { RootState, useAppSelector } from "../slices/store";

import PreLoader from "@component/common/PreLoader";
import ErrorHandler from "@component/common/ErrorHandler";
import Feedback from "@view/interview/panels/components/InterviewFeedback";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { RequestState } from "@utils/types";
import { useMemo } from "react";

const AppHandler: React.FC = () => {
  const auth = useAppSelector((state: RootState) => state.auth);
  const employeeInfoRequestState = useAppSelector(
    (state: RootState) => state.auth.employeeInfoRequestState
  );
  const overallState = useAppSelector(
    (state: RootState) => state.employee.overallState
  );

  const isLoadingInitialData = useMemo(() => {
    if (
      employeeInfoRequestState === RequestState.LOADING ||
      overallState === RequestState.LOADING
    ) {
      return true;
    }
    return false;
  }, [employeeInfoRequestState, overallState]);

  const isInitialDataSuccess = useMemo(() => {
    if (
      employeeInfoRequestState === RequestState.SUCCEEDED &&
      overallState === RequestState.SUCCEEDED
    ) {
      return true;
    }
    return false;
  }, [employeeInfoRequestState, overallState]);

  const isInitialDataFailed = useMemo(() => {
    if (
      employeeInfoRequestState === RequestState.FAILED ||
      overallState === RequestState.FAILED
    ) {
      return true;
    }
    return false;
  }, [employeeInfoRequestState, overallState]);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Layout />,
      errorElement: <Error />,
      children: getActiveRoutesV2(routes, auth.roles),
    },
    {
      path: "/interview/:interviewId/feedback",
      element: <Feedback />,
      errorElement: <Error />,
    },
  ]);

  return (
    <>
      {(auth.status === "loading" || isLoadingInitialData) && (
        <PreLoader isLoading={true} message="We're Getting Things Ready" />
      )}
      {auth.status === "success" && isInitialDataSuccess && (
        <RouterProvider router={router} />
      )}
      {(auth.status === "failed" || isInitialDataFailed) && (
        <ErrorHandler isFullscreen message="Oops! Something went wrong" />
      )}
    </>
  );
};

export default AppHandler;
